import { MediaObjectUrl } from '@kudos/http-client'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { SxProps } from '@mui/material/styles'
import UserAvatar from '@shared/avatar/UserAvatar'
import { TFunction } from '@shared/types/types'
import { fullNameFromParts } from '@shared/utils/nameFormatting'
import { T, useT } from '@transifex/react'
import { isEmpty } from 'lodash/fp'
import { FC, ReactNode } from 'react'
import { Link as RouterLink } from 'react-router'

/**
 * Structural shape that every user-displayable type in the app conforms to.
 *
 * Concrete types satisfy this without casts:
 *   - `TenantMember` (firstName/lastName/email?/avatarUrls?)
 *   - `StaffingRecord` (firstName/lastName/email/avatarUrl/active)
 *   - `TenantMemberDisplayData` / `TimelineTenantMemberDisplayData`
 *   - `UserInfo` (auditing) and other `*DisplayData` embed shapes
 *   - bare `{ firstName, lastName }` literals
 *
 * The component normalizes `avatarUrls`/`avatarUrl` and `active`/`isActive`
 * internally so callers don't need to massage their objects.
 */
export type DisplayableUser = {
  firstName?: string | null
  lastName?: string | null
  fullName?: string | null
  email?: string | null
  avatarUrls?: MediaObjectUrl | null
  avatarUrl?: string | null
  active?: boolean
  isActive?: boolean
  /**
   * Kudos API entity discriminator (e.g. `'tenantMember'`, `'staffRecord'`).
   * When present, it is the authoritative signal used by
   * {@link isStaffingRecord} to decide whether to suppress the
   * "(archived)" suffix. All API-returned entities include it; ad-hoc
   * literals should set it explicitly when they need to mimic a
   * `StaffingRecord` (otherwise they're treated as TenantMember-like).
   */
  __entity_type__?: string
}

export type UserNameDisplayProps = {
  /**
   * The user to display.
   *
   *   - `undefined` — no data available yet (e.g. async load in progress, or
   *     an optional field that wasn't provided). Renders nothing so the
   *     surrounding layout collapses cleanly without a flash of placeholder
   *     text.
   *   - `null` — the user was explicitly deleted upstream. Renders the
   *     localized "Deleted user" placeholder.
   *   - an object with no usable identity (every name field empty) renders
   *     the localized "No name" placeholder.
   */
  user: DisplayableUser | null | undefined
  /** Render a `UserAvatar` next to the name. Defaults to `false`. Suppressed for deleted users. */
  showAvatar?: boolean
  /** When set and the user is live, the name renders as a router `<Link>`. */
  linkTo?: string
  /** Fires when the name (link or plain text) is clicked. Not wired for deleted users. */
  onClick?: () => void
  /** Optional secondary line shown below the name; switches the layout from inline to stacked. Suppressed for deleted users. */
  subText?: ReactNode
  /** Style override applied to the name `<Link>`/`<Typography>`. */
  sx?: SxProps
  /**
   * When `true`, the rendered `<Link>` uses `color="inherit"` instead of the theme primary
   * (`colors.bluePrimary`). Set this on wall feed surfaces (posts, comments, recipients,
   * senders) so the user name blends into the surrounding feed text. Defaults to `false`.
   */
  inheritLinkColor?: boolean
}

/**
 * `true` when there is no usable identity to display: the user is `null`/`undefined`
 * OR every identifying field (`firstName`, `lastName`, `fullName`) is empty.
 */
export const isMissingName = (user: DisplayableUser): boolean => {
  return isEmpty(user.firstName) && isEmpty(user.lastName) && isEmpty(user.fullName)
}

/**
 * Detects whether `user` originated from a `StaffingRecord` (HRIS) vs a
 * `TenantMember` / `*DisplayData` (platform identity).
 *
 * The distinction matters for the "(archived)" suffix: on a `StaffingRecord`,
 * `active: false` means the employee has been terminated in the HRIS, which
 * is not the same as being archived in the Kudos platform. We deliberately
 * do not surface "(archived)" in that case.
 *
 * Detection strategy:
 *   1. **Authoritative** — the API entity discriminator `__entity_type__`.
 *      Every payload returned by `@kudos/http-client` carries this tag
 *      (`'staffRecord'`, `'tenantMember'`, etc.), and it survives spreads,
 *      partial embeds, and optional-field omissions. When present we trust
 *      it exclusively.
 *   2. **Fallback** — for ad-hoc literals that don't carry an entity tag,
 *      we look at multiple structural cues: the presence of singular
 *      `avatarUrl` AND the absence of plural `avatarUrls` AND the absence
 *      of any TenantMember-only field (`isActive`, `id`-with-`active`).
 *      Combining cues protects against a stray `avatarUrl` accidentally
 *      flipping a TenantMember-shaped literal into "StaffingRecord" — and
 *      vice versa for a partial StaffingRecord embed missing `avatarUrl`.
 *      Bare/ambiguous literals default to TenantMember-like (i.e.
 *      "(archived)" applies if `active`/`isActive` is `false`).
 */
const isStaffingRecord = (user: DisplayableUser): boolean => {
  if (user.__entity_type__ !== undefined) {
    return user.__entity_type__ === 'staffRecord'
  }
  const hasAvatarUrl = 'avatarUrl' in user
  const hasAvatarUrls = 'avatarUrls' in user
  const hasTenantMemberOnlyField = 'isActive' in user
  return hasAvatarUrl && !hasAvatarUrls && !hasTenantMemberOnlyField
}

/**
 * `true` when the user should display the localized "(archived)" suffix.
 * Both `isActive: false` (TenantMember archival) and `active: false`
 * (TenantMember*DisplayData archival) qualify. `StaffingRecord`s are
 * intentionally excluded — see {@link isStaffingRecord}.
 */
const isArchivedUser = (user: DisplayableUser): boolean => {
  if (isStaffingRecord(user)) return false
  return user.isActive === false || user.active === false
}

/**
 * String-returning counterpart to `<UserNameDisplay>` for non-JSX contexts:
 * page titles, document titles, toasts, aria-labels, etc.
 *
 *   - `undefined` user — returns an empty string. This represents "no data
 *     available yet" (async load in progress, optional field omitted) and
 *     should not surface a placeholder, otherwise consumers like
 *     `setPageTitle` would briefly render "Deleted user" while a request is
 *     in flight.
 *   - `null` user — returns the localized "Deleted user" label. This
 *     represents an upstream entity that was explicitly removed.
 *   - an object with no usable identity returns the localized "No name".
 *   - otherwise returns the resolved name (with "(archived)" suffix when
 *     applicable).
 */
export const getUserDisplayName = (
  user: DisplayableUser | null | undefined,
  t: TFunction,
): string => {
  if (user === undefined) return ''
  if (user === null) return t('Deleted user')
  if (isMissingName(user)) return t('No name')
  const fullName = user.fullName || fullNameFromParts(user.firstName, user.lastName)
  const base = fullName || user.email || ''
  return isArchivedUser(user) ? `${base} ${t('(archived)')}` : base
}

type NormalizedUser = {
  firstName: string | null | undefined
  lastName: string | null | undefined
  fullName: string | null | undefined
  email: string | null | undefined
  avatarMedia: MediaObjectUrl | null
  avatarSrc: string | undefined
  /** Drives avatar styling (greyed-out for inactive users). Combines both flags. */
  isActive: boolean
  /**
   * Drives the "(archived)" suffix on the rendered name. `StaffingRecord`
   * users are excluded even if `active: false` — see {@link isArchivedUser}.
   */
  isArchived: boolean
}

const normalizeUser = (user: DisplayableUser): NormalizedUser => ({
  firstName: user.firstName,
  lastName: user.lastName,
  fullName: user.fullName,
  email: user.email,
  avatarMedia: user.avatarUrls ?? null,
  avatarSrc: user.avatarUrl ?? undefined,
  isActive: user.isActive ?? user.active ?? true,
  isArchived: isArchivedUser(user),
})

/** Resolves the user's display name without any archived suffix. */
const resolveBaseText = (user: NormalizedUser): string => {
  const fullName = user.fullName || fullNameFromParts(user.firstName, user.lastName)
  return fullName || user.email || ''
}

const DeletedUserText: FC<{ sx?: SxProps }> = ({ sx }) => (
  <Typography component="span" variant="inherit" sx={sx}>
    <T _str="Deleted user" />
  </Typography>
)

const NoNameText: FC<{ sx?: SxProps }> = ({ sx }) => (
  <Typography component="span" variant="inherit" sx={sx}>
    <T _str="No name" />
  </Typography>
)

const UserNameDisplay: FC<UserNameDisplayProps> = ({
  user,
  showAvatar = false,
  linkTo,
  onClick,
  subText,
  sx,
  inheritLinkColor = false,
}) => {
  const t = useT()

  if (user === undefined) {
    return null
  }

  if (user === null) {
    return <DeletedUserText sx={sx} />
  }

  if (isMissingName(user)) {
    return <NoNameText sx={sx} />
  }

  const normalized = normalizeUser(user as DisplayableUser)
  const baseText = resolveBaseText(normalized)

  // Archived users are intentionally de-emphasized: never linked, always
  // plain non-bold text. Caller-supplied `sx` is preserved for layout
  // concerns (overflow, color, etc.) but `fontWeight` is forced to `normal`
  // to override any bold styling from the surrounding context.
  const renderArchivedName = () => (
    <Typography
      component="span"
      variant="inherit"
      translate="no"
      sx={[...(Array.isArray(sx) ? sx : [sx]), { fontWeight: 'normal' }]}>
      {baseText} {t('(archived)')}
    </Typography>
  )

  const renderLinkedName = () => (
    <Link
      component={RouterLink}
      to={linkTo as string}
      onClick={onClick}
      translate="no"
      variant="inherit"
      // The platform standard for user name links is `bluePrimary` (theme `primary.main`).
      // Wall feed surfaces opt out via `inheritLinkColor` so the name blends with feed text.
      color={inheritLinkColor ? 'inherit' : 'primary'}
      sx={sx}>
      {baseText}
    </Link>
  )

  const renderPlainName = () => (
    <Typography component="span" variant="inherit" translate="no" onClick={onClick} sx={sx}>
      {baseText}
    </Typography>
  )

  let nameNode: ReactNode
  if (normalized.isArchived) {
    nameNode = renderArchivedName()
  } else if (linkTo) {
    nameNode = renderLinkedName()
  } else {
    nameNode = renderPlainName()
  }

  const avatarNode = showAvatar ? (
    <UserAvatar
      firstName={normalized.firstName}
      lastName={normalized.lastName}
      fullName={normalized.fullName}
      media={normalized.avatarMedia}
      src={normalized.avatarSrc}
      isActive={normalized.isActive}
    />
  ) : null

  if (subText) {
    return (
      <Stack direction="row" spacing={2} alignItems="center" useFlexGap>
        {avatarNode}
        <Stack minWidth={0}>
          {nameNode}
          <Typography variant="caption">{subText}</Typography>
        </Stack>
      </Stack>
    )
  }

  if (!avatarNode) return nameNode

  return (
    <Stack direction="row" spacing={1} alignItems="center" useFlexGap>
      {avatarNode}
      {nameNode}
    </Stack>
  )
}

export default UserNameDisplay
